export type BaseLayerType = 'plan' | 'google' | 'sentinel' | 'landsat';
export enum BaseMapLayer {
  PLAN = 'plan',
  google = 'googleMap',
  SENTINEL = 'sentinel',
  LANDSAT = 'landsat',
}

export interface IBaseLayer {
  plan?: string;
  google?: string;
  sentinel?: string;
  landsat?: string;
}
export interface IBaseLayerConfig {
  [key: string]: {
    name?: string;
    url?: string;
    subdomains?: string[];
    attribution?: string;
  };
}

import { Marker } from '../interfaces';
import { GisCircleMarker } from '@app/pages/electronic-field-map/pages/gis-fields/interfaces/gis-component.interfaces';
import { ServerTranslateService } from '@services/server-translate.service';
import { Subject } from 'rxjs';
import { GisPolygon } from '@components/openstreetmap/interfaces/gis-component.interfaces';
import { PolygonConverterUtil } from '@app/utils/polygon-converter.util';
import { DEFAULT_MARKER_CLUSTER_OPTIONS } from '../constants/marker-cluster.constants';
import { releaseOsmTileLoad, scheduleOsmTileLoad } from '../constants/osm-tile-request-queue.util';

/**
 * Map Layers Controller - handles layers, markers, and polygons
 * Manages marker rendering, polygon management, and layer operations
 */
export class MapLayersController {
  private markers: Marker[] = [];
  private allPolygons: any[] = [];
  private circleMarkers: GisCircleMarker[] = [];
  private circleMarkersLayer: any;
  private markerClusterGroup: any;
  private markersLayer: any;
  private dynamicPolygonsLayer: any;
  private map: any;
  private showRegionNames: boolean = true;
  private regionLabelMarkers: any[] = []; // Store region label markers
  private serverTranslate?: ServerTranslateService; // For translating region names

  // Base layer URLs for filtering extra tile layers
  private baseLayerUrls: IBaseLayer = {};
  // Current base tile layer (plan / google)
  private baseTileLayer: any = null;
  /** Satellite overlay (Sentinel / Landsat mosaic) rendered above the base layer. */
  private overlayTileLayer: any = null;
  private currentBaseLayerType: BaseLayerType = 'plan';

  // Observable to notify when overlay layer changes
  private overlayLayerChangedSubject$ = new Subject<{ name: string; layer: any } | null>();
  public overlayLayerChanged$ = this.overlayLayerChangedSubject$.asObservable();

  constructor(
    private mapInstance: any,
    serverTranslate?: ServerTranslateService,
  ) {
    this.map = mapInstance;
    this.serverTranslate = serverTranslate;
  }

  /** Wired after bootstrap when the map layers controller is reused from {@link OpenStreetMapController}. */
  setServerTranslate(serverTranslate: ServerTranslateService): void {
    this.serverTranslate = serverTranslate;
  }

  // =========================================================================
  // BASE LAYER URL CONFIGURATION
  // =========================================================================

  /**
   * Set the base layer URLs (plan and satellite) for filtering extra tile layers
   * @param urls - Object containing plan and satellite URLs
   */
  setBaseLayerUrls(urls: IBaseLayer): void {
    this.baseLayerUrls = urls;
  }

  /**
   * Switch the base map layer (plan/satellite) without affecting overlay layers
   * @param layerType - The type of layer ('plan' or 'satellite')
   * @param url - The tile layer URL
   * @param options - Tile layer options
   */
  switchBaseLayer(layerType: BaseLayerType, url: string, options: any = {}): void {
    if (!this.map) {
      return;
    }

    // If this is the first switch, find and remove the initial base layer
    if (!this.baseTileLayer) {
      // Find the existing base tile layer by checking all layers
      this.map.eachLayer((layer: any) => {
        // Check if it's a tile layer with _url property (standard Leaflet tile layers)
        if (layer._url && typeof layer.setOpacity === 'function') {
          const layerDomain = this.extractDomain(layer._url);
          const planDomain = this.extractDomain(this.baseLayerUrls.plan || '');
          const satelliteDomain = this.extractDomain(
            this.baseLayerUrls.google || this.baseLayerUrls.sentinel || this.baseLayerUrls.landsat || '',
          );

          // If this layer matches plan or satellite domain, it's the base layer
          if (layerDomain === planDomain || layerDomain === satelliteDomain) {
            this.map.removeLayer(layer);
          }
        }
      });
    } else {
      // Remove the tracked base tile layer
      this.map.removeLayer(this.baseTileLayer);
    }

    // Build tile layer options
    const tileLayerOptions: any = {
      attribution: options.attribution || '',
      maxZoom: options.maxZoom || 18,
      minZoom: options.minZoom || 1,
      opacity: options.opacity ?? 1,
    };

    // Add subdomains for satellite layer
    if (options.subdomains) {
      tileLayerOptions.subdomains = options.subdomains;
    }

    // Create and add the new base tile layer
    this.baseTileLayer = (window as any).L.tileLayer(url, tileLayerOptions);
    this.baseTileLayer.addTo(this.map);

    // Move base layer to bottom so overlay layers stay on top
    this.baseTileLayer.bringToBack();

    // Update current layer type
    this.currentBaseLayerType = layerType;
  }

  /**
   * Switch the satellite overlay layer (Sentinel / Landsat mosaic) without changing the base map.
   */
  switchOverlayLayer(url: string, options: any = {}): void {
    if (!this.map) {
      return;
    }

    if (this.overlayTileLayer) {
      this.map.removeLayer(this.overlayTileLayer);
      this.overlayTileLayer = null;
    }

    const tileLayerOptions: any = {
      attribution: options.attribution || '',
      maxZoom: options.maxZoom || 18,
      minZoom: options.minZoom || 1,
      opacity: options.opacity ?? 1,
    };

    if (options.subdomains) {
      tileLayerOptions.subdomains = options.subdomains;
    }

    this.overlayTileLayer = (window as any).L.tileLayer(url, tileLayerOptions);
    this.overlayTileLayer.addTo(this.map);

    if (this.baseTileLayer?.bringToBack) {
      this.baseTileLayer.bringToBack();
    }
  }

  /** Remove the satellite overlay layer. */
  clearOverlayLayer(): void {
    if (this.map && this.overlayTileLayer) {
      this.map.removeLayer(this.overlayTileLayer);
    }
    this.overlayTileLayer = null;
  }

  hasOverlayLayer(): boolean {
    return !!this.overlayTileLayer;
  }

  /**
   * Handle weather/overlay layer opacity change
   * @param opacity - Opacity value (0-100)
   */
  onWeatherOpacityChange(opacity: number): void {
    if (!this.map) {
      return;
    }

    // Convert from percentage (0-100) to decimal (0-1)
    const opacityDecimal = opacity / 100;

    // Find and set opacity on all extra tile layers (not plan/satellite)
    const extraLayers = this.getExtraTileLayers();
    extraLayers.forEach((layer) => {
      if (layer.setOpacity) {
        layer.setOpacity(opacityDecimal);
      }
    });
  }

  /**
   * Get all tile layers that are not the base plan/satellite layers
   * @returns Array of extra tile layers
   */
  getExtraTileLayers(): any[] {
    if (!this.map) {
      return [];
    }

    const extraLayers: any[] = [];

    const baseUrls = new Set(
      Object.values(this.baseLayerUrls)
        .filter(Boolean)
        .map((url) => url as string),
    );

    this.map.eachLayer((layer: any) => {
      if (layer === this.baseTileLayer) {
        return;
      }

      // Check if it's a tile layer (has setOpacity function - indicates it's a raster layer)
      if (typeof layer.setOpacity === 'function' && layer.options) {
        // Skip non-tile layers (markers, polygons, etc.)
        if (layer._latlng || layer._latlngs || layer._layers) {
          return;
        }

        // Check for standard tile layers with _url
        if (layer._url) {
          if (!baseUrls.has(layer._url)) {
            extraLayers.push(layer);
          }
          return;
        }

        // Check for custom tile layers (like IndexTileLayer) that have getTileUrl method
        // or have specific className like 'index-tile-layer'
        if (
          layer.getTileUrl ||
          layer.options?.className === 'index-tile-layer' ||
          (layer._tiles && Object.keys(layer._tiles).length > 0)
        ) {
          // This is likely a custom tile layer (index layer, weather layer, etc.)
          // Check if it's not a base layer by checking if it has specific markers
          const hasCustomTileUrl = typeof layer.getTileUrl === 'function';
          const hasIndexClass = layer.options?.className === 'index-tile-layer';
          const hasSatelliteClass = layer.options?.className === 'satellite-tile-layer';

          if (hasCustomTileUrl || hasIndexClass || hasSatelliteClass) {
            extraLayers.push(layer);
          }
        }
      }
    });

    return extraLayers;
  }

  /**
   * Extract domain from URL for comparison
   */
  extractDomain(url: string): string {
    try {
      if (!url) return '';
      // Remove protocol and get domain
      const withoutProtocol = url.replace(/^https?:\/\//, '');
      // Get domain part (handle {s} subdomain placeholder)
      const domain = withoutProtocol
        .split('/')[0]
        .replace('{s}.', '')
        .replace('mt{s}.', 'mt.')
        .replace(/\{[^}]+\}/g, ''); // Remove any other placeholders
      return domain.toLowerCase();
    } catch {
      return '';
    }
  }

  // =========================================================================
  // OVERLAY LAYER OPERATIONS (layers beyond plan/satellite)
  // =========================================================================

  // =========================================================================
  // CUSTOM LAYER OPERATIONS (user-added overlay layers)
  // =========================================================================

  // Store custom layers for tracking
  private customLayers: Map<string, any> = new Map();

  /**
   * Add a custom tile layer with custom getTileUrl function
   * @param layerId - Unique identifier for the layer
   * @param getTileUrl - Function that returns URL for given coordinates {x, y, z}
   * @param options - Optional tile layer options (opacity, attribution, etc.)
   * @returns The created layer instance
   */
  addCustomTileLayer(
    layerId: string,
    getTileUrl: (coords: { x: number; y: number; z: number }) => string,
    options: any = {},
  ): any {
    if (!this.map) return null;
    if (this.customLayers.has(layerId)) return null;

    const L = (window as any).L;
    const { throttleTiles, ...tileOptions } = options;

    const CustomTileLayer = L.TileLayer.extend({
      getTileUrl: function (coords: any) {
        return getTileUrl(coords);
      },

      createTile: function (coords: any, done: (error: Error | null, tile?: HTMLElement) => void) {
        const tile = document.createElement('img') as HTMLImageElement;
        tile.alt = '';
        tile.setAttribute('role', 'presentation');

        const url = this.getTileUrl(coords);
        const finish = (error: Error | null) => {
          if (throttleTiles) {
            releaseOsmTileLoad();
          }
          done(error, tile);
        };

        const assignSrc = () => {
          tile.onload = () => finish(null);
          tile.onerror = () => finish(new Error(`Tile load failed: ${url}`));
          tile.src = url;
        };

        if (throttleTiles) {
          scheduleOsmTileLoad(assignSrc);
        } else {
          assignSrc();
        }

        return tile;
      },

      // Переопределяем внутренние методы GridLayer, добавляя проверку на наличие карты
      _updateLevels: function () {
        if (!this._map) {
          return;
        } // Если карта уже удалена, выходим
        return L.TileLayer.prototype._updateLevels.apply(this, arguments);
      },

      _setView: function () {
        if (!this._map) {
          return;
        } // Если карта уже удалена, выходим
        return L.TileLayer.prototype._setView.apply(this, arguments);
      },
    });

    const customLayer = new CustomTileLayer('', tileOptions);
    customLayer._customLayerId = layerId;
    customLayer.addTo(this.map);
    this.customLayers.set(layerId, customLayer);

    return customLayer;
  }

  /**
   * Remove a custom layer by ID
   * @param layerId - The unique identifier of the layer to remove
   * @returns boolean indicating success/failure
   */
  removeCustomLayer(layerId: string): boolean {
    if (!this.map) return false;
    const layer = this.customLayers.get(layerId);

    if (layer) {
      this.map.removeLayer(layer);
      this.customLayers.delete(layerId);
    }
    return true;
  }

  /**
   * Remove all custom layers
   */
  removeAllCustomLayers(): void {
    this.customLayers.forEach((layer, layerId) => {
      if (this.map && layer) {
        this.map.removeLayer(layer);
      }
    });
    this.customLayers.clear();
  }

  /**
   * Check if a custom layer exists
   * @param layerId - The unique identifier of the layer
   */
  hasCustomLayer(layerId: string): boolean {
    return this.customLayers.has(layerId);
  }

  /** Set opacity (0–1) on a single tracked custom tile layer. */
  setCustomLayerOpacity(layerId: string, opacity: number): void {
    const layer = this.customLayers.get(layerId);
    if (layer && typeof layer.setOpacity === 'function') {
      layer.setOpacity(Math.max(0, Math.min(1, opacity)));
    }
  }

  /** Set opacity (0–1) on every tracked custom tile layer whose id starts with {@link prefix}. */
  setCustomLayersOpacityByPrefix(prefix: string, opacity: number): void {
    const clamped = Math.max(0, Math.min(1, opacity));
    this.customLayers.forEach((layer, layerId) => {
      if (layerId.startsWith(prefix) && typeof layer.setOpacity === 'function') {
        layer.setOpacity(clamped);
      }
    });
  }
  // =========================================================================
  // MARKER OPERATIONS
  // =========================================================================

  /**
   * Add multiple markers to the map
   */
  addMarkers(markers: Marker[]): void {
    this.markers = markers;
    this.renderMarkers();
  }

  /**
   * Add a single marker to the map
   */
  addMarker(marker: Marker): void {
    this.markers.push(marker);
    this.renderMarkers();
  }

  /**
   * Add or replace markers by id, then redraw once — keeps unrelated markers (weather, etc.).
   */
  mergeMarkers(markers: Marker[]): void {
    if (!markers?.length) return;
    for (const marker of markers) {
      const idx = this.markers.findIndex((m) => m.id === marker.id);
      if (idx >= 0) {
        this.markers[idx] = marker;
      } else {
        this.markers.push(marker);
      }
    }
    this.renderMarkers();
  }

  /**
   * Remove a marker by ID
   */
  removeMarker(markerId: string): void {
    this.markers = this.markers.filter((m) => m.id !== markerId);
    this.renderMarkers();
  }

  removeMarkers(markers: string[]): void {
    markers.forEach((markerId) => {
      this.markers = this.markers.filter((m) => m.id !== markerId);
    });
    // Do NOT call clearMarkers() here — it resets this.markers to [] and would wipe unrelated markers.
    // renderMarkers() clears the Leaflet marker/cluster layers before repainting from this.markers.
    this.renderMarkers();
  }

  /** Open a bound popup on a marker by id (e.g. deep link from crop-identification list). */
  openMarkerPopup(markerId: string): boolean {
    const markerData = this.markers.find((m) => m.id === markerId);
    if (!markerData) {
      return false;
    }
    const leafletMarker = (
      markerData as Marker & {
        _leafletMarker?: { openPopup: () => void; bindPopup: (html: string) => void; getPopup?: () => unknown };
      }
    )._leafletMarker;
    if (!leafletMarker || typeof leafletMarker.openPopup !== 'function') {
      return false;
    }
    const html = markerData.popup || markerData.title;
    if (!html) {
      return false;
    }
    if (!leafletMarker.getPopup?.()) {
      leafletMarker.bindPopup(html);
    }
    leafletMarker.openPopup();
    return true;
  }

  /** Open a bound popup on a GIS/dynamic polygon by id. */
  openPolygonPopup(polygonId: string): boolean {
    const polygonIdStr = String(polygonId);
    const entry = this.allPolygons.find((p) => String(p.properties?.id) === polygonIdStr);
    if (!entry?.polygon) {
      return false;
    }
    const leafletPolygon = entry.polygon as {
      openPopup: () => void;
      bindPopup: (html: string) => void;
      getPopup?: () => unknown;
    };
    const html = entry.properties?.popup || entry.properties?.label;
    if (!html) {
      return false;
    }
    if (!leafletPolygon.getPopup?.()) {
      leafletPolygon.bindPopup(html);
    }
    leafletPolygon.openPopup();
    return true;
  }

  /**
   * Clear all markers from the map
   */
  clearMarkers(): void {
    this.markers = [];
    if (this.markersLayer) {
      this.markersLayer.clearLayers();
    }
    if (this.markerClusterGroup) {
      this.markerClusterGroup.clearLayers();
    }
  }

  /**
   * Internal: Render all markers on the map
   */
  private renderMarkers(): void {
    // Auto-initialize layers if not set
    if (!this.markersLayer && this.map) {
      this.initializeMarkerLayers();
    }

    if (!this.markersLayer) {
      return;
    }

    this.markersLayer.clearLayers();
    if (this.markerClusterGroup) {
      this.markerClusterGroup.clearLayers();
    }

    this.markers.forEach((markerData) => {
      try {
        if (
          typeof markerData.lat !== 'number' ||
          typeof markerData.lng !== 'number' ||
          Number.isNaN(markerData.lat) ||
          Number.isNaN(markerData.lng)
        ) {
          console.warn('[MapLayersController] Skip marker with invalid lat/lng', markerData?.id);
          return;
        }

        let markerIcon;

        // Check if custom HTML icon is provided
        if (markerData.customIcon) {
          const iconSz: [number, number] =
            markerData.customIconSize?.length === 2 ? markerData.customIconSize : ([32, 32] as [number, number]);
          const iconAc: [number, number] =
            markerData.customIconAnchor?.length === 2
              ? markerData.customIconAnchor
              : ([Math.round(iconSz[0] / 2), Math.round(iconSz[1] / 2)] as [number, number]);
          const popupAc: [number, number] =
            markerData.customIconPopupAnchor?.length === 2
              ? markerData.customIconPopupAnchor
              : ([0, -Math.round(iconSz[1] / 2)] as [number, number]);

          markerIcon = (window as any).L.divIcon({
            html: markerData.customIcon,
            className: 'custom-marker-icon',
            iconSize: iconSz,
            iconAnchor: iconAc,
            popupAnchor: popupAc,
          });
        } else {
          // Use default SVG icon with color
          const color = markerData.color || '#2196F3';
          markerIcon = this.createMarkerIcon(color);
        }

        const monitoringFlag =
          !!(markerData as Marker).monitoring || !!(markerData.data?.monitoring ?? markerData.data?.isMonitoring);
        const planFactMismatchFlag =
          !!(markerData as Marker).planFactCropMismatch || !!markerData.data?.planFactCropMismatch;
        const marker = (window as any).L.marker([markerData.lat, markerData.lng], {
          icon: markerIcon,
          pane: (markerData as any).pane || 'markerPane',
          interactive: (markerData as any).interactive !== undefined ? (markerData as any).interactive : true,
          monitoring: monitoringFlag,
          planFactCropMismatch: planFactMismatchFlag,
        });

        // Weather stations: `withCluster: false`. Field parcels: `customIcon` + `withCluster: true` keeps clustering enabled.
        const useCluster =
          markerData.withCluster !== false && (!markerData.customIcon || markerData.withCluster === true);

        // Apply custom zIndex if provided
        if ((markerData as any).zIndex !== undefined) {
          marker.setZIndexOffset((markerData as any).zIndex);
        }

        // Bind popup if provided — in clusters hover-open is off by default (`openPopupOnHover: true` required for hover with clusters).
        if (markerData.popup) {
          marker.bindPopup(markerData.popup);
          const inClusterLayer = !!(useCluster && this.markerClusterGroup);
          const hoverPopupAllowed =
            markerData.openPopupOnHover !== false && (!inClusterLayer || markerData.openPopupOnHover === true);
          if (hoverPopupAllowed) {
            marker.on('mouseover', () => {
              marker.openPopup();
            });
            marker.on('mouseout', () => {
              marker.closePopup();
            });
          }
        }

        // Attach click handler if provided
        if (markerData.onClick && typeof markerData.onClick === 'function') {
          marker.on('click', () => {
            markerData.onClick(markerData);
          });
        }

        (markerData as Marker & { _leafletMarker?: unknown })._leafletMarker = marker;

        if (useCluster && this.markerClusterGroup) {
          // Add to marker cluster layer
          this.markerClusterGroup.addLayer(marker);
        } else {
          // Add directly without clustering
          marker.addTo(this.markersLayer);
        }
      } catch (e) {
        console.warn('[MapLayersController] Failed to render marker', markerData?.id, e);
      }
    });
  }

  /**
   * Initialize marker layers if they don't exist
   */
  private initializeMarkerLayers(): void {
    if (!this.map) {
      console.error('Cannot initialize marker layers: map instance is undefined');
      return;
    }

    // Create marker pane if it doesn't exist
    if (!this.map.getPane('markerPane')) {
      const markerPane = this.map.createPane('markerPane');
      markerPane.style.zIndex = 950;
    }

    // Create markers layer
    this.markersLayer = (window as any).L.layerGroup({ pane: 'markerPane' }).addTo(this.map);

    // Create marker cluster group if available
    if (typeof (window as any).L.markerClusterGroup !== 'undefined') {
      this.markerClusterGroup = (window as any).L.markerClusterGroup({
        ...DEFAULT_MARKER_CLUSTER_OPTIONS,
        pane: 'markerPane',
      });
      this.map.addLayer(this.markerClusterGroup);
    }
  }

  /**
   * Internal: Create a marker icon with specified color
   */
  private createMarkerIcon(color: string): any {
    const svgString = `
      <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="8" fill="${color}" stroke="#ffffff" stroke-width="2"/>
        <circle cx="12" cy="12" r="3" fill="#ffffff"/>
      </svg>
    `;
    const svgBase64 = 'data:image/svg+xml;base64,' + btoa(svgString);

    return (window as any).L.icon({
      iconUrl: svgBase64,
      iconSize: [24, 24],
      iconAnchor: [12, 12],
      popupAnchor: [0, -12],
    });
  }

  // =========================================================================
  // POLYGON OPERATIONS
  // =========================================================================

  /**
   * Add a dynamic polygon to the map
   */
  addDynamicPolygon(
    coordinates: [number, number][],
    properties: Partial<GisPolygon> = {},
    eventsController?: any,
  ): void {
    // 1. Ensure dynamicPolygonsLayer and custom panes are initialized
    if (!this.dynamicPolygonsLayer) {
      if (!this.map.getPane('dynamicPolygonsPane')) {
        const polygonsPane = this.map.createPane('dynamicPolygonsPane');
        polygonsPane.style.zIndex = 900;
        polygonsPane.style.pointerEvents = 'none'; // Disable pointer events on pane level
      }
      this.dynamicPolygonsLayer = (window as any).L.layerGroup({ pane: 'dynamicPolygonsPane' }).addTo(this.map);
    }

    // Create anomaly highlight pane if requested (higher z-index)
    if (properties.pane === 'anomalyHighlightPane' && !this.map.getPane('anomalyHighlightPane')) {
      const anomalyPane = this.map.createPane('anomalyHighlightPane');
      anomalyPane.style.zIndex = 1100;
    }

    /** Field boundary under anomaly fills — above dynamic polygons (900), below anomaly highlights (1100). */
    if (properties.pane === 'anomalyFieldOutlinePane' && !this.map.getPane('anomalyFieldOutlinePane')) {
      const fieldOutlinePane = this.map.createPane('anomalyFieldOutlinePane');
      fieldOutlinePane.style.zIndex = 1000;
    }

    const normalizedCoords = this.normalizeCoordinates(coordinates);
    const isRectangle = this.isAxisAlignedRectangle(normalizedCoords);

    // 2. Define Polygon options
    const polygonOptions: any = {
      color: properties.strokeColor || properties.color || '#3388ff',
      weight: properties.strokeWidth || properties.weight || 2,
      opacity: properties.opacity !== undefined ? properties.opacity : 0.8,
      fillColor: properties.fillColor,
      fillOpacity: properties.fillOpacity !== undefined ? properties.fillOpacity : 0.5,
      fill: properties.fill,
      interactive: properties.interactive !== false,
      title: properties.name || '',
      bubblingMouseEvents: false,
      pane: properties.pane || 'dynamicPolygonsPane',
    };

    if (properties.dashArray) {
      polygonOptions.dashArray = properties.dashArray;
    }

    // 3. Create the Shape (Rectangle or Polygon)
    let shape: any;

    if (isRectangle && normalizedCoords.length >= 4) {
      const bounds = (window as any).L.latLngBounds(normalizedCoords);
      shape = (window as any).L.rectangle(bounds, polygonOptions);
    } else {
      const latLngs = normalizedCoords.map((coord) => (window as any).L.latLng(coord[0], coord[1]));
      if (properties.wktCoordinates) {
        shape = (window as any).L.polygon(properties.wktCoordinates, polygonOptions);
      } else {
        shape = (window as any).L.polygon(latLngs, polygonOptions);
      }
    }

    // 4. SVG Path customization on add
    shape.on('add', () => {
      const pathElement = shape._path || shape.getElement();
      if (pathElement) {
        // Set specific pointer events to allow clicking the fill but potentially ignoring the stroke
        pathElement.style.pointerEvents = 'visibleFill';
        pathElement.style.strokeLinecap = 'butt';
        pathElement.style.strokeLinejoin = 'miter';

        pathElement.classList.add('polygon-fill-only-interactive');
        pathElement.setAttribute('pointer-events', 'fill');
        pathElement.setAttribute('stroke-opacity', polygonOptions.opacity);

        if (properties.planFactDiagonalStripes && pathElement instanceof SVGPathElement) {
          this.applySeasonPlanFactStripedFill(pathElement, properties);
        }
      }

      // Handle visibility based on zoom level
      this.updatePolygonVisibilityByZoom(shape, properties);
    });

    // 5. Attach Metadata to the shape object
    if (properties.id) shape._polygonId = properties.id;
    if (properties.name) {
      shape._polygonName = properties.name;
    }
    if (properties.data) shape._polygonData = properties.data;

    // Store zoom constraints
    if (properties.showByZoomLevel) {
      shape._showByZoomLevel = true;
      shape._minZoom = properties.minZoom || 0;
      shape._maxZoom = properties.maxZoom || 19;
    }

    // 6. Set up Event Listeners (Hover / Click)
    const getGisPolygonData = (e?: any) => ({
      ...properties,
      coordinates: normalizedCoords,
      clickedLatLng: e?.latlng ? [e.latlng.lat, e.latlng.lng] : undefined,
      clickEvent: e,
    });

    if (properties.onHover) {
      shape.on('mouseover', () => properties.onHover!(getGisPolygonData() as any));
    }
    if (properties.onHoverOut) {
      shape.on('mouseout', () => properties.onHoverOut!(getGisPolygonData() as any));
    }
    if (properties.onClick) {
      shape.on('click', (e: any) => properties.onClick!(getGisPolygonData(e) as any));
    }

    if (properties.popup) {
      shape.bindPopup(properties.popup);
      if (properties.openPopupOnHover !== false) {
        shape.on('mouseover', () => {
          shape.openPopup();
        });
        shape.on('mouseout', () => {
          shape.closePopup();
        });
      }
    }

    // Emit events through external controller if provided
    if (eventsController) {
      shape.on('click', (e: any) => {
        eventsController.emitPolygonClick({
          id: properties.id,
          name: properties.name,
          data: properties.data,
          fillColor: properties?.fillColor,
          coordinates: normalizedCoords,
          clickedLatLng: e?.latlng ? [e.latlng.lat, e.latlng.lng] : undefined,
        });
      });
    }

    // 7. ADD LABEL LOGIC (Text displayed directly on map)
    if (properties.label) {
      const L = (window as any).L;
      const center = shape.getBounds().getCenter();
      const labelPane =
        properties.pane === 'anomalyHighlightPane' ? 'anomalyHighlightPane' : this.ensurePolygonFieldLabelPane();

      const fontSize =
        typeof properties.labelSize === 'number' && Number.isFinite(properties.labelSize as number)
          ? `${properties.labelSize}px`
          : typeof properties.labelSize === 'string' && (properties.labelSize as string).trim()
            ? String(properties.labelSize).trim()
            : '12px';

      const labelIcon = L.divIcon({
        className: 'polygon-label-wrapper',
        html: `<div style="
              transform: translate(-50%, -50%);
              color: ${properties.labelColor || '#111827'};
              font-size: ${fontSize};
              font-weight: bold;
              text-shadow: -1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff, 1px 1px 0 #fff;
              white-space: nowrap;
              pointer-events: none;
            ">${properties.label}</div>`,
        iconSize: [0, 0],
        iconAnchor: [0, 0],
      });

      const labelMarker = L.marker(center, {
        icon: labelIcon,
        pane: labelPane,
        interactive: false,
      });

      // Link label lifecycle to the polygon
      shape.on('remove', () => {
        if (this.dynamicPolygonsLayer.hasLayer(labelMarker)) {
          this.dynamicPolygonsLayer.removeLayer(labelMarker);
        }
      });

      labelMarker.addTo(this.dynamicPolygonsLayer);
      shape._labelMarker = labelMarker;
    }

    // 8. Finalize: Add to map and internal tracking array
    try {
      shape.addTo(this.dynamicPolygonsLayer);
      this.allPolygons.push({ polygon: shape, coordinates: normalizedCoords, properties });
    } catch (error) {
      console.error('Error adding polygon to map:', error);
    }
  }
  /**
   * Clear all dynamic polygons from the map
   */
  clearDynamicPolygons(): void {
    if (this.dynamicPolygonsLayer) {
      this.dynamicPolygonsLayer.clearLayers();
    }
    this.allPolygons = [];
  }

  /**
   * Remove a specific polygon by ID
   */
  removeDynamicPolygon(polygonId: string | number): void {
    if (!this.dynamicPolygonsLayer) {
      return;
    }

    // Convert polygonId to string for comparison to handle both string and number IDs
    const polygonIdStr = String(polygonId);

    // Find the polygon in the allPolygons array
    const polygonIndex = this.allPolygons.findIndex((p) => String(p.properties?.id) === polygonIdStr);

    if (polygonIndex !== -1) {
      const polygonData = this.allPolygons[polygonIndex];

      // Remove from the layer
      if (polygonData.polygon) {
        this.dynamicPolygonsLayer.removeLayer(polygonData.polygon);
      }

      // Remove from the array
      this.allPolygons.splice(polygonIndex, 1);
    }
  }

  /**
   * Update an existing polygon's coordinates and/or properties
   * @param polygonId - The ID of the polygon to update
   * @param coordinates - New coordinates for the polygon (optional)
   * @param properties - New/updated properties for the polygon (optional)
   */
  updatePolygon(polygonId: string | number, coordinates?: [number, number][], properties?: Partial<GisPolygon>): void {
    if (!this.dynamicPolygonsLayer) {
      return;
    }

    // Convert polygonId to string for comparison
    const polygonIdStr = String(polygonId);

    // Find the polygon in the allPolygons array
    const polygonIndex = this.allPolygons.findIndex((p) => String(p.properties?.id) === polygonIdStr);

    if (polygonIndex === -1) {
      return;
    }

    const polygonData = this.allPolygons[polygonIndex];
    const oldPolygon = polygonData.polygon;

    // If coordinates are provided, update them
    const newCoordinates = coordinates ? this.normalizeCoordinates(coordinates) : polygonData.coordinates;

    // Merge old properties with new properties
    const mergedProperties = {
      ...polygonData.properties,
      ...properties,
    };

    // Create new polygon options
    const polygonOptions: any = {
      color: mergedProperties.strokeColor || mergedProperties.color || '#3388ff',
      weight: mergedProperties.strokeWidth || mergedProperties.weight || 2,
      opacity: mergedProperties.opacity !== undefined ? mergedProperties.opacity : 0.8,
      fillColor: mergedProperties.fillColor,
      fillOpacity: mergedProperties.fillOpacity !== undefined ? mergedProperties.fillOpacity : 0.5,
      fill: mergedProperties.fill,
      interactive: mergedProperties.interactive !== false,
      title: mergedProperties.name || '',
      bubblingMouseEvents: false,
      pane: mergedProperties.pane || 'dynamicPolygonsPane',
    };

    if (mergedProperties.dashArray) {
      polygonOptions.dashArray = mergedProperties.dashArray;
    }

    // Check if it's a rectangle
    const isRectangle = this.isAxisAlignedRectangle(newCoordinates);

    // Create new shape
    let newShape: any;
    if (isRectangle && newCoordinates.length >= 4) {
      const bounds = [
        [newCoordinates[0][0], newCoordinates[0][1]],
        [newCoordinates[2][0], newCoordinates[2][1]],
      ];
      newShape = (window as any).L.rectangle(bounds, polygonOptions);
    } else {
      newShape = (window as any).L.polygon(newCoordinates, polygonOptions);
    }

    // Copy over event handlers and properties from old polygon
    if (mergedProperties.onClick) {
      newShape.on('click', (e: any) => {
        mergedProperties.onClick({
          coordinates: newCoordinates,
          properties: mergedProperties,
          data: mergedProperties.data,
          originalEvent: e,
        });
      });
    }

    if (mergedProperties.onHover) {
      newShape.on('mouseover', (e: any) => {
        mergedProperties.onHover({
          coordinates: newCoordinates,
          properties: mergedProperties,
          data: mergedProperties.data,
          originalEvent: e,
        });
      });
    }

    if (mergedProperties.onHoverOut) {
      newShape.on('mouseout', (e: any) => {
        mergedProperties.onHoverOut({
          coordinates: newCoordinates,
          properties: mergedProperties,
          data: mergedProperties.data,
          originalEvent: e,
        });
      });
    }

    // Bind popup if provided
    if (mergedProperties.popup) {
      newShape.bindPopup(mergedProperties.popup);
      if (mergedProperties.openPopupOnHover !== false) {
        newShape.on('mouseover', () => {
          newShape.openPopup();
        });
        newShape.on('mouseout', () => {
          newShape.closePopup();
        });
      }
    }

    // Bind tooltip if provided
    if (mergedProperties.tooltip) {
      newShape.bindTooltip(mergedProperties.tooltip, {
        permanent: false,
        direction: 'top',
      });
    }

    // Add properties to the shape
    if (mergedProperties.id) {
      newShape._polygonId = mergedProperties.id;
    }
    if (mergedProperties.name) {
      newShape._polygonName = mergedProperties.name;
    }
    if (mergedProperties.data) {
      newShape._polygonData = mergedProperties.data;
    }

    // Store zoom visibility properties
    if (mergedProperties.showByZoomLevel) {
      newShape._showByZoomLevel = true;
      newShape._minZoom = mergedProperties.minZoom || 0;
      newShape._maxZoom = mergedProperties.maxZoom || 19;
    }

    // Remove old polygon from layer
    if (oldPolygon) {
      this.dynamicPolygonsLayer.removeLayer(oldPolygon);
    }

    // Add new polygon to layer
    this.dynamicPolygonsLayer.addLayer(newShape);

    // Update the stored polygon data
    this.allPolygons[polygonIndex] = {
      polygon: newShape,
      coordinates: newCoordinates,
      properties: mergedProperties,
    };
  }

  /**
   * Get all polygons
   */
  getAllPolygons(): any[] {
    return [...this.allPolygons];
  }

  /**
   * Set whether to show region names as tooltips
   */
  setShowRegionNames(show?: boolean): void {
    this.showRegionNames = show as boolean;
    this.updatePolygonTooltips();
  }

  /**
   * Filled vs outline-only for every polygon in {@link allPolygons} (dynamic / GIS paths).
   */
  applyPolygonFillVisibility(fillVisible: boolean): void {
    const L = (window as any).L;
    if (!L) {
      return;
    }

    for (const entry of this.allPolygons) {
      const shape = entry?.polygon;
      if (!shape || typeof shape.setStyle !== 'function') {
        continue;
      }

      const props = entry.properties || {};

      if (fillVisible) {
        const fo =
          shape._osmStoredFillOpacity !== undefined
            ? shape._osmStoredFillOpacity
            : props.fillOpacity !== undefined
              ? props.fillOpacity
              : 0.5;
        const fillOn = shape._osmStoredFill !== undefined ? shape._osmStoredFill : props.fill !== false;
        shape.setStyle({ fillOpacity: fo, fill: fillOn });
        delete shape._osmStoredFillOpacity;
        delete shape._osmStoredFill;
      } else {
        if (shape._osmStoredFillOpacity === undefined) {
          const current = shape.options || {};
          shape._osmStoredFillOpacity =
            current.fillOpacity !== undefined
              ? current.fillOpacity
              : props.fillOpacity !== undefined
                ? props.fillOpacity
                : 0.5;
          shape._osmStoredFill = current.fill !== false;
        }
        shape.setStyle({ fillOpacity: 0, fill: true });
      }
    }
  }

  /**
   * Update tooltip visibility for all polygons based on showRegionNames setting
   * Creates static labels at polygon centers like Google Maps
   */
  private updatePolygonTooltips(): void {
    // Clear existing region label markers
    this.clearRegionLabelMarkers();

    if (!this.showRegionNames) {
      return; // Don't show labels if disabled
    }

    // Get current zoom level for responsive sizing and visibility
    const currentZoom = this.map.getZoom();

    // Create static label markers for each region polygon
    this.allPolygons.forEach(({ polygon, properties, coordinates }) => {
      if (properties.name && properties.data?.region && properties.type === 'region') {
        // Calculate polygon center using centroid algorithm
        const center = PolygonConverterUtil.getPolygonCenter(coordinates);

        // Get translated region name
        const translatedName = this.getTranslatedRegionName(properties.data.region);

        // Determine zoom class for responsive font sizing
        let zoomClass = 'zoom-medium';
        if (currentZoom < 7) {
          zoomClass = 'zoom-small';
        } else if (currentZoom > 10) {
          zoomClass = 'zoom-large';
        }

        // Create a static HTML label marker at the center
        const labelIcon = (window as any).L.divIcon({
          html: `<div class="region-label-static text-only ${zoomClass}">${translatedName}</div>`,
          className: 'region-label-marker',
          iconSize: null,
          iconAnchor: [0, 0],
        });

        const labelMarker = (window as any).L.marker([center?.lat, center?.lng], {
          icon: labelIcon,
          interactive: false,
          pane: 'tooltipPane',
          zIndexOffset: 1200, // Above polygons and markers
        });

        // Store reference to polygon and region data
        labelMarker._regionId = properties.data.region.id;
        labelMarker._regionName = translatedName;
        labelMarker._center = center;

        // Store zoom visibility properties from the polygon
        if (polygon._showByZoomLevel) {
          labelMarker._showByZoomLevel = true;
          labelMarker._minZoom = polygon._minZoom || 0;
          labelMarker._maxZoom = polygon._maxZoom || 19;
        }

        // Add to map only if visible at current zoom
        const shouldShow =
          !labelMarker._showByZoomLevel || (currentZoom >= labelMarker._minZoom && currentZoom <= labelMarker._maxZoom);

        if (shouldShow) {
          labelMarker.addTo(this.map);
        }

        // Store in array for later removal
        this.regionLabelMarkers.push(labelMarker);
      }
    });
  }

  /**
   * Clear all region label markers from the map
   */
  private clearRegionLabelMarkers(): void {
    this.regionLabelMarkers.forEach((marker) => {
      if (marker && this.map) {
        this.map.removeLayer(marker);
      }
    });
    this.regionLabelMarkers = [];
  }

  /**
   * Calculate the center point of a polygon using centroid algorithm
   */
  public calculatePolygonCenter(coordinates: [number, number][]): [number, number] {
    if (!coordinates || coordinates.length === 0) {
      return [0, 0];
    }

    // Use centroid algorithm for better accuracy
    let area = 0;
    let centroidLat = 0;
    let centroidLng = 0;

    for (let i = 0; i < coordinates.length; i++) {
      const j = (i + 1) % coordinates.length;
      const [lat1, lng1] = coordinates[i];
      const [lat2, lng2] = coordinates[j];

      const crossProduct = lat1 * lng2 - lat2 * lng1;
      area += crossProduct;
      centroidLat += (lat1 + lat2) * crossProduct;
      centroidLng += (lng1 + lng2) * crossProduct;
    }

    area = area / 2;

    if (Math.abs(area) < 0.000001) {
      // Fallback to simple average if area is too small
      let latSum = 0;
      let lngSum = 0;
      coordinates.forEach(([lat, lng]) => {
        latSum += lat;
        lngSum += lng;
      });
      return [latSum / coordinates.length, lngSum / coordinates.length];
    }

    centroidLat = centroidLat / (6 * area);
    centroidLng = centroidLng / (6 * area);

    return [centroidLat, centroidLng];
  }

  /**
   * Get translated region name using ServerTranslateService
   */
  private getTranslatedRegionName(region: any): string {
    if (!region) {
      return 'Unknown Region';
    }

    // Use ServerTranslate if available
    if (this.serverTranslate && region.names) {
      const translated = this.serverTranslate.translate(region, 'names');
      return (translated as string) || region.name || 'Unknown Region';
    }

    // Fallback to region.name
    return region.name || 'Unknown Region';
  }

  /**
   * Update region labels when language changes
   */
  updateRegionLabelsLanguage(): void {
    if (!this.showRegionNames || this.regionLabelMarkers.length === 0) {
      return;
    }

    const currentZoom = this.map.getZoom();
    let zoomClass = 'zoom-medium';
    if (currentZoom < 7) {
      zoomClass = 'zoom-small';
    } else if (currentZoom > 10) {
      zoomClass = 'zoom-large';
    }

    this.regionLabelMarkers.forEach((marker) => {
      if (marker._icon) {
        const polygonData = this.allPolygons.find((p) => p.properties?.data?.region?.id === marker._regionId);

        if (polygonData && polygonData.properties?.data?.region) {
          const translatedName = this.getTranslatedRegionName(polygonData.properties.data.region);

          const labelDiv = marker._icon.querySelector('.region-label-static');
          if (labelDiv) {
            labelDiv.textContent = translatedName;
            labelDiv.classList.remove('zoom-small', 'zoom-medium', 'zoom-large');
            labelDiv.classList.add(zoomClass);
          }

          marker._regionName = translatedName;
        }
      }
    });
  }

  // =========================================================================
  // COORDINATE UTILITIES
  // =========================================================================

  /**
   * Internal: Normalize coordinates to [lat, lng] format
   * Ensures polygon is properly closed (first and last point are identical)
   */
  private normalizeCoordinates(coordinates: any[]): [number, number][] {
    if (!coordinates || coordinates.length === 0) {
      return [];
    }

    const normalized = coordinates.map((coord) => {
      let lat: number, lng: number;

      if (Array.isArray(coord)) {
        const val0 = Number(coord[0]);
        const val1 = Number(coord[1]);

        // Detect if coordinates are in [lng, lat] or [lat, lng] format
        if (Math.abs(val0) > 90 && Math.abs(val1) <= 90) {
          lng = val0;
          lat = val1;
        } else {
          lat = val0;
          lng = val1;
        }
      } else if (typeof coord === 'object') {
        lat = Number(coord.lat || coord.latitude || 0);
        lng = Number(coord.lng || coord.longitude || 0);
      } else {
        return [0, 0] as [number, number];
      }

      return [Math.max(-90, Math.min(90, lat)), lng] as [number, number];
    });

    // Ensure polygon is properly closed (first and last points must be identical)
    if (normalized.length > 2) {
      const first = normalized[0];
      const last = normalized[normalized.length - 1];

      // Check if first and last points are different
      const tolerance = 0.0001; // Small tolerance for floating point comparison
      if (Math.abs(first[0] - last[0]) > tolerance || Math.abs(first[1] - last[1]) > tolerance) {
        // Add the first point at the end to close the polygon
        normalized.push([first[0], first[1]]);
      }
    }

    return normalized;
  }

  /** Pane for polygon HTML labels above {@link dynamicPolygonsPane} (900) so labels stay visible after SVG redraw */
  private ensurePolygonFieldLabelPane(): string {
    const paneName = 'fieldPolygonLabelsPane';
    if (!this.map.getPane(paneName)) {
      const el = this.map.createPane(paneName);
      el.style.zIndex = '920';
      el.style.pointerEvents = 'none';
    }
    return paneName;
  }

  /**
   * Internal: Check if coordinates form an axis-aligned rectangle
   */
  private isAxisAlignedRectangle(coordinates: [number, number][]): boolean {
    if (coordinates.length < 4) return false;
    const round = (num: number) => Math.round(num * 1e8) / 1e8;
    const lats = new Set(coordinates.map((c) => round(c[0])));
    const lngs = new Set(coordinates.map((c) => round(c[1])));
    return lats.size === 2 && lngs.size === 2;
  }

  // =========================================================================
  // LAYER SETTERS
  // =========================================================================

  /**
   * Set the markers layer
   */
  setMarkersLayer(layer: any): void {
    this.markersLayer = layer;
  }

  /**
   * Set the marker cluster group
   */
  setMarkerClusterGroup(group: any): void {
    this.markerClusterGroup = group;
  }

  /**
   * Set the dynamic polygons layer
   */
  setDynamicPolygonsLayer(layer: any): void {
    this.dynamicPolygonsLayer = layer;
  }

  /**
   * Set opacity for a specific layer by name or layer instance
   * @param layerNameOrInstance - The layer name ('markers', 'polygons') or a Leaflet layer instance
   * @param opacity - Opacity value between 0 and 1
   */
  setOpacity(layerNameOrInstance: string | any, opacity: number): void {
    // Clamp opacity between 0 and 1
    const clampedOpacity = Math.max(0, Math.min(1, opacity));

    if (typeof layerNameOrInstance === 'string') {
      // Handle by layer name
      switch (layerNameOrInstance.toLowerCase()) {
        case 'base':
        case 'base-layer':
          if (this.baseTileLayer?.setOpacity) {
            this.baseTileLayer.setOpacity(clampedOpacity);
          }
          break;

        case 'overlay':
        case 'satellite':
        case 'satellite-image':
          if (this.overlayTileLayer?.setOpacity) {
            this.overlayTileLayer.setOpacity(clampedOpacity);
            break;
          }
          if (this.baseTileLayer?.setOpacity) {
            this.baseTileLayer.setOpacity(clampedOpacity);
            break;
          }
          this.map.eachLayer((layer: any) => {
            if (
              layer?._url &&
              typeof layer.setOpacity === 'function' &&
              Object.values(this.baseLayerUrls).includes(layer._url)
            ) {
              layer.setOpacity(clampedOpacity);
            }
          });
          break;

        case 'markers':
          if (this.markersLayer) {
            this.markersLayer.setOpacity(clampedOpacity);
          }
          if (this.markerClusterGroup) {
            this.markerClusterGroup.setOpacity(clampedOpacity);
          }
          break;

        case 'polygons':
        case 'dynamic-polygons':
          if (this.dynamicPolygonsLayer) {
            this.dynamicPolygonsLayer.setOpacity(clampedOpacity);
          }
          // Also update all individual polygons
          this.allPolygons.forEach(({ polygon }) => {
            if (polygon && polygon.setStyle) {
              const currentStyle = polygon.options;
              polygon.setStyle({
                opacity: clampedOpacity,
                fillOpacity: clampedOpacity * (currentStyle.fillOpacity || 0.5),
              });
            }
          });
          break;

        default:
          break;
      }
    } else if (layerNameOrInstance && typeof layerNameOrInstance === 'object') {
      // Handle Leaflet layer instance
      if (layerNameOrInstance.setOpacity) {
        layerNameOrInstance.setOpacity(clampedOpacity);
      } else if (layerNameOrInstance.setStyle) {
        // For polygon/shape layers
        layerNameOrInstance.setStyle({
          opacity: clampedOpacity,
          fillOpacity: clampedOpacity * 0.5,
        });
      } else {
        return;
      }
    }
  }

  // =========================================================================
  // Season GIS — plan vs fact: diagonal hatch on polygon fill
  // =========================================================================

  /**
   * Fill color reflects actual crop (“fact”); diagonal stripes use planned crop color (“plan”).
   */
  private applySeasonPlanFactStripedFill(pathEl: SVGPathElement, properties: Partial<GisPolygon>): void {
    const spec = properties.planFactDiagonalStripes;
    if (!spec) {
      return;
    }

    let fact = this.sanitizeSvgColor(spec.factFillColor);
    let plan = this.sanitizeSvgColor(spec.planStripeColor);
    if (!fact) {
      fact = '#4287f5';
    }
    if (!plan) {
      plan = '#9c27b0';
    }

    const svg = pathEl.ownerSVGElement;
    if (!svg) {
      return;
    }

    let defs = svg.querySelector('defs');
    if (!defs) {
      defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
      svg.insertBefore(defs, svg.firstChild as ChildNode);
    }

    const safeIdSuffix = String(properties.id ?? 'pf').replace(/[^a-zA-Z0-9_-]/g, '_');
    const patternId = `gis-planfact-stripes-${safeIdSuffix}`;
    if (typeof document !== 'undefined') {
      document.getElementById(patternId)?.remove();
    }

    const ns = 'http://www.w3.org/2000/svg';
    const pattern = document.createElementNS(ns, 'pattern');
    pattern.setAttribute('id', patternId);
    pattern.setAttribute('patternUnits', 'userSpaceOnUse');
    const pw = '12';
    const ph = '12';
    pattern.setAttribute('width', pw);
    pattern.setAttribute('height', ph);

    const bg = document.createElementNS(ns, 'rect');
    bg.setAttribute('width', pw);
    bg.setAttribute('height', ph);
    bg.setAttribute('fill', fact);
    const bgOpacity = typeof spec.backgroundFillOpacity === 'number' ? String(spec.backgroundFillOpacity) : '0.88';
    bg.setAttribute('fill-opacity', bgOpacity);

    const stripeW = typeof spec.stripeStrokeWidth === 'number' ? String(spec.stripeStrokeWidth) : '4';
    const stripeOp = typeof spec.stripeStrokeOpacity === 'number' ? String(spec.stripeStrokeOpacity) : '1';
    const dash =
      typeof spec.stripeDashArray === 'string' && spec.stripeDashArray.trim() ? spec.stripeDashArray.trim() : '4 6';

    const addStripeLine = (d: string) => {
      const p = document.createElementNS(ns, 'path');
      p.setAttribute('d', d);
      p.setAttribute('stroke', plan);
      p.setAttribute('stroke-width', stripeW);
      p.setAttribute('stroke-linecap', 'butt');
      p.setAttribute('stroke-linejoin', 'miter');
      p.setAttribute('stroke-dasharray', dash);
      p.setAttribute('fill', 'none');
      p.setAttribute('opacity', stripeOp);
      pattern.appendChild(p);
    };

    pattern.appendChild(bg);
    /* Fine diagonal hatch: dashed strokes (traits), not wide solid bands */
    addStripeLine('M -2 14 L14 -2');
    addStripeLine('M 5 15 L15 5');
    defs.appendChild(pattern);

    pathEl.setAttribute('fill', `url(#${patternId})`);
    pathEl.style.removeProperty('fill-opacity');
  }

  private sanitizeSvgColor(color: string | undefined): string | null {
    if (!color || typeof color !== 'string') {
      return null;
    }
    const t = color.trim();
    if (/^#[0-9A-Fa-f]{3}([0-9A-Fa-f]{3})?$/.test(t) || /^#[0-9A-Fa-f]{8}$/.test(t)) {
      return t;
    }
    return null;
  }

  // =========================================================================
  // ZOOM-BASED VISIBILITY
  // =========================================================================

  /**
   * Update polygon visibility based on current zoom level
   */
  private updatePolygonVisibilityByZoom(polygon: any, properties: any): void {
    if (!properties.showByZoomLevel) {
      return;
    }

    const currentZoom = this.map.getZoom();
    const minZoom = properties.minZoom || 0;
    const maxZoom = properties.maxZoom || 19;

    if (currentZoom >= minZoom && currentZoom <= maxZoom) {
      this.showPolygon(polygon);
    } else {
      this.hidePolygon(polygon);
    }
  }

  /**
   * Update all polygons visibility based on current zoom level
   */
  updateAllPolygonsVisibilityByZoom(): void {
    const currentZoom = this.map.getZoom();

    this.allPolygons.forEach(({ polygon }) => {
      if (polygon._showByZoomLevel) {
        const minZoom = polygon._minZoom || 0;
        const maxZoom = polygon._maxZoom || 19;

        if (currentZoom >= minZoom && currentZoom <= maxZoom) {
          this.showPolygon(polygon);
        } else {
          this.hidePolygon(polygon);
        }
      }
    });

    // Update region labels visibility based on zoom level
    this.updatePolygonTooltips();
  }

  /**
   * Show a polygon
   */
  private showPolygon(polygon: any): void {
    if (polygon && polygon._path) {
      polygon._path.style.display = '';
    }
  }

  /**
   * Hide a polygon
   */
  private hidePolygon(polygon: any): void {
    if (polygon && polygon._path) {
      polygon._path.style.display = 'none';
    }
  }

  // =========================================================================
  // MAP NAVIGATION
  // =========================================================================

  /**
   * Center the map on a specific point with optional zoom level
   */
  centerToPoint(lat: number, lng: number, zoom?: number, animate: boolean = true): void {
    if (!this.map) {
      return;
    }

    const targetZoom = zoom || this.map.getZoom() || 13;

    if (animate) {
      this.map.setView([lat, lng], targetZoom, {
        animate: true,
        duration: 0.8,
        easeLinearity: 0.5,
      });
    } else {
      this.map.setView([lat, lng], targetZoom);
    }
  }

  zoom(zoom?: number, animate: boolean = true): void {
    if (!this.map) {
      return;
    }

    const targetZoom = zoom || this.map.getZoom() || 13;

    if (animate) {
      this.map.setView(this.map.getCenter(), targetZoom, {
        animate: true,
        duration: 0.8,
        easeLinearity: 0.5,
      });
    } else {
      this.map.setView(this.map.getCenter(), targetZoom);
    }
  }

  /**
   * Center the map on a multipolygon, calculating optimal bounds and zoom level
   * Uses fitBounds to ensure the entire multipolygon is visible in the viewport
   * @param multiCoordinates - Array of polygons, each polygon is an array of rings (outer ring + holes)
   * @param animate - animation
   * @param padding - padding around the bounds
   */
  centerToMultiPolygon(
    multiCoordinates: [number, number][][][],
    animate: boolean = true,
    padding: [number, number] = [50, 50],
  ): void {
    if (!this.map) {
      return;
    }

    if (!multiCoordinates || multiCoordinates.length === 0) {
      return;
    }

    // Calculate bounding box across all polygons
    let south = Infinity;
    let north = -Infinity;
    let west = Infinity;
    let east = -Infinity;

    for (const polygon of multiCoordinates) {
      if (polygon.length > 0) {
        const outerRing = polygon[0]; // Use outer ring only
        const normalizedCoords = this.normalizeCoordinates(outerRing);
        for (const [lat, lng] of normalizedCoords) {
          if (lat < south) south = lat;
          if (lat > north) north = lat;
          if (lng < west) west = lng;
          if (lng > east) east = lng;
        }
      }
    }

    // Check if we found valid bounds
    if (!isFinite(south) || !isFinite(north) || !isFinite(west) || !isFinite(east)) {
      return;
    }

    // Create bounds from bounding box
    const bounds = (window as any).L.latLngBounds(
      [south, west], // Southwest corner
      [north, east], // Northeast corner
    );

    if (animate) {
      this.map.fitBounds(bounds, {
        padding: padding,
        animate: true,
        duration: 0.8,
        easeLinearity: 0.5,
      });
    } else {
      this.map.fitBounds(bounds, {
        padding: padding,
        animate: false,
      });
    }
  }

  /**
   * Flash a polygon for visual feedback (0.6 seconds)
   * @param coordinates - Normalized coordinates to identify the polygon
   */

  /**
   * Fit the map view to show all provided coordinates
   */
  fitBounds(coordinates: [number, number][], padding: [number, number] = [50, 50], animate: boolean = true): void {
    if (!this.map || !coordinates || coordinates.length === 0) {
      return;
    }

    const normalizedCoords = this.normalizeCoordinates(coordinates);
    const bounds = (window as any).L.latLngBounds(normalizedCoords);

    if (animate) {
      this.map.fitBounds(bounds, {
        padding: padding,
        animate: true,
        duration: 0.8,
      });
    } else {
      this.map.fitBounds(bounds, {
        padding: padding,
      });
    }
  }

  // =========================================================================
  // CIRCLE MARKER OPERATIONS
  // =========================================================================

  /**
   * Add a single circle marker to the map
   */
  addCircleMarker(circleMarker: GisCircleMarker): void {
    if (!this.circleMarkersLayer) {
      this.initializeCircleMarkersLayer();
    }

    this.circleMarkers.push(circleMarker);
    this.renderCircleMarker(circleMarker);
  }

  /**
   * Add multiple circle markers to the map
   */
  addCircleMarkers(circleMarkers: GisCircleMarker[]): void {
    if (!this.circleMarkersLayer) {
      this.initializeCircleMarkersLayer();
    }

    this.circleMarkers.push(...circleMarkers);
    circleMarkers.forEach((circle) => this.renderCircleMarker(circle));
  }

  /**
   * Remove a circle marker by ID
   */
  removeCircleMarker(circleId: string): void {
    this.circleMarkers = this.circleMarkers.filter((c) => c.id !== circleId);

    if (this.circleMarkersLayer) {
      this.circleMarkersLayer.eachLayer((layer: any) => {
        if (layer._circleId === circleId) {
          this.circleMarkersLayer.removeLayer(layer);
        }
      });
    }
  }

  /**
   * Clear all circle markers from the map
   */
  clearCircleMarkers(): void {
    this.circleMarkers = [];
    if (this.circleMarkersLayer) {
      this.circleMarkersLayer.clearLayers();
    }
  }

  /**
   * Initialize circle markers layer
   */
  private initializeCircleMarkersLayer(): void {
    if (!this.map) {
      return;
    }

    // Create circle markers pane if it doesn't exist
    if (!this.map.getPane('circleMarkersPane')) {
      const circlePane = this.map.createPane('circleMarkersPane');
      circlePane.style.zIndex = 900; // Increased z-index for CircleMarker
    }

    // Create circle markers layer
    this.circleMarkersLayer = (window as any).L.layerGroup({ pane: 'circleMarkersPane' }).addTo(this.map);
  }

  /**
   * Render a single circle marker on the map
   */
  private renderCircleMarker(circleMarker: GisCircleMarker): void {
    if (!this.circleMarkersLayer) {
      return;
    }

    const L = (window as any).L;

    // Create circle with fixed radius in pixels (won't grow with zoom)
    const circle = L.circleMarker(circleMarker.center, {
      radius: 20, // Fixed radius in pixels
      fillColor: circleMarker.fillColor || '#4CAF50',
      color: circleMarker.strokeColor || '#2E7D32',
      fillOpacity: circleMarker.fillOpacity || 0.6,
      weight: circleMarker.strokeWidth || 2,
      pane: 'circleMarkersPane',
    });

    // Store circle ID for later reference
    circle._circleId = circleMarker.id;

    // Store zoom visibility properties
    if (circleMarker.showByZoomLevel) {
      circle._showByZoomLevel = true;
      circle._minZoom = circleMarker.minZoom || 0;
      circle._maxZoom = circleMarker.maxZoom || 19;
    }

    // Add text label with count in the center of the circle
    const divIcon = L.divIcon({
      className: 'circle-count-label',
      html: `<div style="
        background: white;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: 14px;
        color: ${circleMarker.fillColor};
        border: 2px solid ${circleMarker.strokeColor || '#2E7D32'};
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      ">${circleMarker.count}</div>`,
      iconSize: [40, 40],
      iconAnchor: [20, 20],
    });

    const labelMarker = L.marker(circleMarker.center, {
      icon: divIcon,
      pane: 'circleMarkersPane',
    });

    // Store reference to label marker
    circle._labelMarker = labelMarker;
    labelMarker._circleId = circleMarker.id;

    // Store zoom visibility for label too
    if (circleMarker.showByZoomLevel) {
      labelMarker._showByZoomLevel = true;
      labelMarker._minZoom = circleMarker.minZoom || 0;
      labelMarker._maxZoom = circleMarker.maxZoom || 19;
    }

    const tooltipContent =
      circleMarker.tooltip ??
      `
      <strong>${circleMarker.data?.regionName || ''}</strong><br>
       ${circleMarker.count || ''}
    `;

    circle.bindTooltip(tooltipContent, {
      permanent: false,
      direction: 'top',
      className: 'circle-marker-tooltip',
    });

    // Bind the same tooltip to the label marker (white circle with number)
    labelMarker.bindTooltip(tooltipContent, {
      permanent: false,
      direction: 'top',
      className: 'circle-marker-tooltip',
    });

    // Add click handler
    if (circleMarker.onClick) {
      circle.on('click', () => {
        circleMarker.onClick!(circleMarker);
      });
      // Add click handler to label marker too
      labelMarker.on('click', () => {
        circleMarker.onClick!(circleMarker);
      });
    }

    // Add hover handler
    if (circleMarker.onHover) {
      circle.on('mouseover', () => {
        circleMarker.onHover!(circleMarker);
      });
      // Add hover handler to label marker too
      labelMarker.on('mouseover', () => {
        circleMarker.onHover!(circleMarker);
      });
    }

    // Add both circle and label to layer
    circle.addTo(this.circleMarkersLayer);
    labelMarker.addTo(this.circleMarkersLayer);

    // Update visibility based on current zoom
    // Use setTimeout to ensure DOM elements (_path, _icon) are created before updating visibility
    setTimeout(() => {
      this.updateCircleVisibilityByZoom(circle);
      this.updateCircleVisibilityByZoom(labelMarker);
    }, 0);
  }

  /**
   * Update circle visibility based on zoom level
   */
  private updateCircleVisibilityByZoom(circle: any): void {
    if (!circle._showByZoomLevel) {
      return;
    }

    const currentZoom = this.map.getZoom();
    const minZoom = circle._minZoom || 0;
    const maxZoom = circle._maxZoom || 19;

    const shouldShow = currentZoom >= minZoom && currentZoom <= maxZoom;

    if (circle._path) {
      circle._path.style.display = shouldShow ? '' : 'none';
    }
    if (circle._icon) {
      circle._icon.style.display = shouldShow ? '' : 'none';
    }
  }

  /**
   * Update all circle markers visibility based on current zoom level
   */
  updateAllCircleMarkersVisibilityByZoom(): void {
    if (!this.circleMarkersLayer) {
      return;
    }

    this.circleMarkersLayer.eachLayer((layer: any) => {
      this.updateCircleVisibilityByZoom(layer);
    });
  }

  // =========================================================================
  // POLYGON INTERACTIVITY CONTROL
  // =========================================================================

  /**
   * Disable polygon interactivity to allow drawing over them
   * This fixes the cursor issue where regions prevent drawing
   */
  disablePolygonInteractivity(): void {
    // Get the polygons pane and disable pointer events on it
    const polygonsPane = this.map.getPane('dynamicPolygonsPane');
    if (polygonsPane) {
      polygonsPane.style.pointerEvents = 'none';
    }

    // Also disable on each polygon for safety
    this.allPolygons.forEach(({ polygon }) => {
      if (polygon && polygon._path) {
        polygon._path.style.pointerEvents = 'none';
      }
      if (polygon && polygon.options) {
        polygon.options.interactive = false;
      }
    });
  }

  /**
   * Enable polygon interactivity after drawing is complete
   */
  enablePolygonInteractivity(): void {
    // Re-enable pointer events on the polygons pane
    const polygonsPane = this.map.getPane('dynamicPolygonsPane');
    if (polygonsPane) {
      polygonsPane.style.pointerEvents = 'auto';
    }

    // Re-enable on each polygon
    this.allPolygons.forEach(({ polygon, properties }) => {
      if (polygon && polygon._path) {
        // Re-enable pointer events (use 'fill' to keep only fill interactive)
        polygon._path.style.pointerEvents = properties?.interactive === false ? 'none' : 'fill';
      }
      if (polygon && polygon.options) {
        polygon.options.interactive = properties?.interactive !== false;
      }
    });
  }
}
